import { useState, useRef, useEffect } from 'react';

import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';

import {
  Button,
  Select,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Box,
  RadioGroup,
  Text,
  Spinner,
  VStack,
  Divider,
} from '@chakra-ui/react';

import { apiUpdateCallListItem, apiGetCallListItemLeads } from 'api';

import type { APICallListData, APICallListLeadData } from 'types/api.types';

import {
  CALL_LIST_STATUSES,
  CALL_LIST_STATUS_CONNECTED_DECLINED,
  CALL_LIST_STATUS_CONNECTED_QUOTED,
  CALL_LIST_STATUS_UNABLE_TO_CONNECT,
} from 'views/call_list/constants';

const ItemStatusChange = ({
  item,
  queryKey,
  onAction,
}: {
  item: APICallListData;
  queryKey: unknown[];
  onAction?: () => void;
}) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [newStatus, setNewStatus] = useState<string | undefined>(undefined);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState<number | null>(null);
  const [skipLeadSelection, setSkipLeadSelection] = useState(false);

  const cancelRef = useRef<HTMLButtonElement>(null);

  const {
    data: leads,
    isLoading: isLoadingLeads,
    error: leadsError,
  } = useQuery({
    queryKey: ['call-list-leads', item.id],
    queryFn: () => apiGetCallListItemLeads(item.id),
    enabled:
      isConfirmOpen && Number(newStatus) === CALL_LIST_STATUS_CONNECTED_QUOTED,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  useEffect(() => {
    if (!isConfirmOpen) {
      setSelectedLeadId(null);
      setSkipLeadSelection(false);
    }
  }, [isConfirmOpen]);

  const updateStatusMutation = useMutation({
    mutationFn: () => {
      if (!newStatus) return Promise.reject('No status selected');

      const updateData =
        Number(newStatus) === CALL_LIST_STATUS_CONNECTED_QUOTED &&
        selectedLeadId &&
        !skipLeadSelection &&
        leads &&
        leads.length > 0
          ? { status: Number(newStatus), lead: selectedLeadId }
          : { status: Number(newStatus) };

      return apiUpdateCallListItem(item.id, updateData);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, (oldData: any) => {
        if (!oldData) return oldData;

        // For certain statuses remove the item from the list
        if (
          [
            CALL_LIST_STATUS_CONNECTED_DECLINED,
            CALL_LIST_STATUS_CONNECTED_QUOTED,
            CALL_LIST_STATUS_UNABLE_TO_CONNECT,
          ].includes(data.status)
        ) {
          return {
            ...oldData,
            results: oldData.results.filter(
              (item: APICallListData) => item.id !== data.id,
            ),
          };
        }

        // Update item on list cache
        return {
          ...oldData,
          results: oldData.results.map((item: APICallListData) =>
            item.id === data.id ? data : item,
          ),
        };
      });

      toast({
        title: 'Status updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });

      setNewStatus(undefined);
      setIsConfirmOpen(false);
      setSelectedLeadId(null);
      setSkipLeadSelection(false);

      if (onAction) onAction();
    },
    onError: (error: any) => {
      let errorMessage = 'Unknown error occurred';

      if (error?.status) {
        errorMessage = error.status[0];
      } else if (error?.lead) {
        errorMessage = error.lead[0];
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      toast({
        title: 'Error updating status',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });

      setIsConfirmOpen(false);
    },
  });

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedStatus = e.target.value;
    setNewStatus(selectedStatus);

    const requiresConfirmation = [
      CALL_LIST_STATUS_CONNECTED_DECLINED,
      CALL_LIST_STATUS_CONNECTED_QUOTED,
      CALL_LIST_STATUS_UNABLE_TO_CONNECT,
    ].includes(Number(selectedStatus));

    if (requiresConfirmation) {
      setIsConfirmOpen(true);
    } else {
      updateStatusMutation.mutate();
    }
  };

  const getTargetStatusLabel = () => {
    return (
      CALL_LIST_STATUSES.find(
        (status) => Number(status.value) === Number(newStatus),
      )?.label || 'Unknown'
    );
  };

  const handleLeadChange = (value: string) => {
    setSelectedLeadId(Number(value));
    setSkipLeadSelection(false);
  };

  const handleNoValidOptions = () => {
    setSelectedLeadId(null);
    setSkipLeadSelection(true);
  };

  const canConfirm = () => {
    if (Number(newStatus) === CALL_LIST_STATUS_CONNECTED_QUOTED) {
      return (
        selectedLeadId !== null ||
        skipLeadSelection ||
        (leads && leads.length === 0)
      );
    }
    return true;
  };

  return (
    <Box w="100%">
      <Select
        value={String(item.status)}
        onChange={handleStatusChange}
        isDisabled={updateStatusMutation.isPending}
        variant="main"
      >
        {CALL_LIST_STATUSES.map((status) => (
          <option key={status.value} value={status.value}>
            {status.label}
          </option>
        ))}
      </Select>

      <AlertDialog
        isOpen={isConfirmOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setIsConfirmOpen(false)}
        size={
          Number(newStatus) === CALL_LIST_STATUS_CONNECTED_QUOTED ? 'xl' : 'md'
        }
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirm Status Change
            </AlertDialogHeader>

            <AlertDialogBody>
              <Text mb={4}>
                Are you sure you want to change the status from "
                {item.status_display}" to "{getTargetStatusLabel()}"?
              </Text>

              {Number(newStatus) === CALL_LIST_STATUS_CONNECTED_QUOTED && (
                <Box mt={4}>
                  <Text fontWeight="bold" mb={2}>
                    Select a lead:
                  </Text>

                  {isLoadingLeads && (
                    <Box textAlign="center" py={4}>
                      <Spinner size="md" />
                      <Text mt={2}>Loading leads...</Text>
                    </Box>
                  )}

                  {leadsError && (
                    <Box bg="red.50" p={3} borderRadius="md">
                      <Text color="red.500">
                        Error loading leads. Please try again.
                      </Text>
                    </Box>
                  )}

                  {leads && leads.length === 0 && (
                    <Box bg="yellow.50" p={3} borderRadius="md">
                      <Text>No leads available for this item.</Text>
                    </Box>
                  )}

                  {leads && leads.length > 0 && (
                    <>
                      <RadioGroup
                        onChange={handleLeadChange}
                        value={selectedLeadId?.toString()}
                      >
                        <VStack
                          align="stretch"
                          spacing={2}
                          maxH="250px"
                          overflowY="auto"
                          mb={4}
                        >
                          {leads.map((lead: APICallListLeadData) => (
                            <Box
                              key={lead.id}
                              p={3}
                              borderWidth="1px"
                              borderRadius="md"
                              cursor="pointer"
                              onClick={() =>
                                handleLeadChange(lead.id.toString())
                              }
                              bg={
                                selectedLeadId === lead.id && !skipLeadSelection
                                  ? 'blue.50'
                                  : 'white'
                              }
                              borderColor={
                                selectedLeadId === lead.id && !skipLeadSelection
                                  ? 'blue.500'
                                  : 'gray.200'
                              }
                              _hover={{
                                borderColor: 'blue.300',
                                bg:
                                  selectedLeadId === lead.id &&
                                  !skipLeadSelection
                                    ? 'blue.50'
                                    : 'gray.50',
                              }}
                              transition="all 0.2s"
                              display="flex"
                              alignItems="center"
                              opacity={skipLeadSelection ? 0.5 : 1}
                            >
                              <Box
                                borderWidth="2px"
                                borderRadius="full"
                                w="18px"
                                h="18px"
                                mr={3}
                                borderColor={
                                  selectedLeadId === lead.id &&
                                  !skipLeadSelection
                                    ? 'blue.500'
                                    : 'gray.300'
                                }
                                bg={
                                  selectedLeadId === lead.id &&
                                  !skipLeadSelection
                                    ? 'blue.500'
                                    : 'white'
                                }
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                              >
                                {selectedLeadId === lead.id &&
                                  !skipLeadSelection && (
                                    <Box as="span" fontSize="xs" color="white">
                                      ✓
                                    </Box>
                                  )}
                              </Box>
                              <Box
                                flex="1"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                              >
                                <Text fontWeight="medium">
                                  {lead.first_name} {lead.last_name}
                                </Text>
                                <Text fontSize="sm" color="gray.600" ml={2}>
                                  {lead.workflow}
                                </Text>
                              </Box>
                            </Box>
                          ))}
                        </VStack>
                      </RadioGroup>

                      <Divider my={4} />

                      <Box
                        p={3}
                        borderWidth="1px"
                        borderRadius="md"
                        cursor="pointer"
                        onClick={handleNoValidOptions}
                        bg={skipLeadSelection ? 'orange.50' : 'white'}
                        borderColor={
                          skipLeadSelection ? 'orange.500' : 'gray.200'
                        }
                        _hover={{
                          borderColor: 'orange.300',
                          bg: skipLeadSelection ? 'orange.50' : 'gray.50',
                        }}
                        transition="all 0.2s"
                        display="flex"
                        alignItems="center"
                      >
                        <Box
                          borderWidth="2px"
                          borderRadius="full"
                          w="18px"
                          h="18px"
                          mr={3}
                          borderColor={
                            skipLeadSelection ? 'orange.500' : 'gray.300'
                          }
                          bg={skipLeadSelection ? 'orange.500' : 'white'}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          {skipLeadSelection && (
                            <Box as="span" fontSize="xs" color="white">
                              ✓
                            </Box>
                          )}
                        </Box>
                        <Text
                          fontWeight="medium"
                          color={skipLeadSelection ? 'orange.800' : 'gray.700'}
                        >
                          No valid options
                        </Text>
                      </Box>
                    </>
                  )}
                </Box>
              )}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setIsConfirmOpen(false)}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={() => updateStatusMutation.mutate()}
                isLoading={updateStatusMutation.isPending}
                isDisabled={!canConfirm()}
                ml={3}
              >
                Confirm
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default ItemStatusChange;
